import React from 'react';

import CarouselRightSmall from '@rover/icons/svg/carousel-right-small.svg';
import type { SvgIcon } from '@rover/types/src/SvgIcon';
import { Box, Flex, Image, Text } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';

import { getSizeStyles, Props, Size } from './utils';

const renderGraphic = (size: Size, rounded: boolean, graphic: string | SvgIcon): JSX.Element => {
  const sizeStyles = getSizeStyles(size);
  if (typeof graphic === 'string') {
    return (
      <Image
        testID="image"
        description="mediablock image"
        src={graphic}
        {...(rounded && { borderRadius: 'round' })}
        {...sizeStyles}
      />
    );
  }
  const ImageIcon = graphic;
  return <ImageIcon {...sizeStyles} />;
};

const MediaBlock = ({
  title,
  subtitle,
  subtitleTextColor = 'tertiary',
  graphic,
  size = Size.Default,
  rounded = false,
  textSize = '200',
  onClick,
  href,
  ariaLabel,
  role,
  children,
  dataTestId,
}: Props): JSX.Element => {
  const theme = useTheme();
  const textProps = { size: textSize };

  const hoverStyle =
    onClick || href
      ? {
          // This hover is needed in case an "a" tag is returned
          '&:hover': {
            background: theme.background.interactive.primary_pressed,
          },
        }
      : {};

  const content = (
    <Flex
      flexDirection="row"
      alignItems={subtitle ? 'flex-start' : 'center'}
      justifyContent="space-between"
      aria-hidden={ariaLabel !== undefined}
      sx={{
        fontSize: 'initial',
        fontWeight: 'initial',
        lineHeight: 'initial',
        ...hoverStyle,
      }}
      width="100%"
      data-testid={dataTestId}
    >
      <Box
        display="inline-block"
        justifyContent="flex-start"
        alignItems="self-start"
        mr="5x"
        height="100%"
      >
        {renderGraphic(size, rounded, graphic)}
      </Box>
      <Box display="flex" flexDirection="column" alignItems="flex-start" flexGrow="1" width="100%">
        {title && (
          <Text fontWeight="semibold" {...textProps}>
            {title}
          </Text>
        )}
        {subtitle && (
          <Text textColor={subtitleTextColor} {...textProps}>
            {subtitle}
          </Text>
        )}
        {typeof children === 'string' ? <Text {...textProps}>{children}</Text> : children}
      </Box>
      {(onClick || href) && (
        <Box alignSelf="center">
          <CarouselRightSmall />
        </Box>
      )}
    </Flex>
  );

  const commonProps = {
    'aria-label': ariaLabel,
    'data-testid': 'mediaBlockWrapper',
    webProps: { role },
    tabIndex: 0,
  } as const;

  const hoverStyleProps = {
    border: 'none',
    boxShadow: 'initial',
    borderRadius: 'initial',
    cursor: 'pointer',
    textDecoration: 'none',
    color: theme.textColor.secondary,
  };

  if (onClick) {
    return (
      <Box onClick={onClick} p="0x" {...commonProps} sx={hoverStyleProps} width="100%">
        {content}
      </Box>
    );
  }
  if (href) {
    return (
      <a href={href} {...commonProps} style={hoverStyleProps}>
        {content}
      </a>
    );
  }
  return <div {...commonProps}>{content}</div>;
};

export default MediaBlock;
